// import { Dialog, DialogPortal, DialogTitle } from "@/components/ui/dialog";
import useAppStore from "@/store/app-store";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "@pkg/icons";
import { AnimatePresence, motion } from "motion/react";
import { ReactNode, useEffect, useState } from "react";
import { cn } from "../../../utils/cn";

export interface ModalProps {
  children?: ReactNode;
  modalState?: boolean;
  handleCloseModal?: () => void;
  title?: string;
  noCloseButton?: boolean;
  isCenterAligned?: boolean;
  closeOnEsc?: boolean;
  onModalClose?: () => void;
  closeButtonClass?: string;
  theme?: string;
  className?: string;
  overlayClassName?: string;
  mobileDrawer?: boolean; // Makes modal slide from bottom like a drawer on screens < 640px
  hasCustomTitle?: boolean; // Children render their own DialogPrimitive.DialogTitle, so skip the empty fallback
}

const DrawerDialog = (props: ModalProps) => {
  const {
    modalState,
    handleCloseModal,
    onModalClose = () => {},
    closeButtonClass = "text-black",
    title,
    children,
    noCloseButton = false,
    closeOnEsc = true,
    isCenterAligned = false,
    className,
    overlayClassName,
    theme = "light",
    mobileDrawer = false,
    hasCustomTitle = false,
  } = props;

  // Get Intercom store
  const setHideIntercom = useAppStore((state) => state.setHideIntercom);

  // Track if screen is mobile (< 640px)
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check initial screen size
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    checkMobile();

    // Listen for resize
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Handle body scroll lock and Intercom visibility when modal is open
  useEffect(() => {
    if (modalState) {
      // Store original body overflow
      const originalOverflow = document.body.style.overflow;
      const originalPaddingRight = document.body.style.paddingRight;

      // Get scrollbar width to prevent layout shift
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;

      // Lock scroll
      document.body.style.overflow = "hidden";
      // Add padding to compensate for scrollbar removal
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }

      // Hide Intercom when modal opens
      setHideIntercom(true);

      // Cleanup: restore original overflow and show Intercom when modal closes
      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.paddingRight = originalPaddingRight;
        setHideIntercom(false);
      };
    }
  }, [modalState, setHideIntercom]);

  const onClose = (e?: any) => {
    e?.stopPropagation?.();
    handleCloseModal?.();
    onModalClose();
  };

  // Determine if we should use drawer mode (mobile + mobileDrawer prop)
  const isDrawerMode = mobileDrawer && isMobile;

  // Animation variants for drawer mode
  const drawerVariants = {
    hidden: { y: "100%", opacity: 0 },
    visible: { y: 0, opacity: 1 },
    exit: { y: "100%", opacity: 0 },
  };

  // Animation variants for modal mode
  const modalVariants = {
    hidden: { scale: 1, opacity: 0 },
    visible: { scale: 1, opacity: 1 },
    exit: { scale: 0.95, opacity: 0 },
  };

  // Handle drag start for drawer - prevent default to stop pull-to-refresh
  const handleDragStart = (event: any) => {
    if (noCloseButton) return;
    event.preventDefault?.();
  };

  // Handle drag end for drawer
  const handleDragEnd = (_event: any, info: any) => {
    // If dragged down more than 150px or velocity is high, close the drawer
    const shouldClose = info.offset.y > 130 || info.velocity.y > 400;

    if (shouldClose && closeOnEsc) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {modalState && (
        <DialogPrimitive.Dialog
          open={true}
          onOpenChange={(open) => !open && closeOnEsc && onClose()}
        >
          <DialogPrimitive.DialogPortal forceMount>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className={cn("fixed inset-0 z-50 bg-black/30", overlayClassName)}
              onClick={closeOnEsc ? onClose : undefined}
            />

            {/* Container positioning */}
            <div
              className={cn(
                "pointer-events-none fixed right-0 left-0 z-50",
                isDrawerMode
                  ? "top-auto bottom-0" // Drawer: at bottom
                  : "top-0 max-h-full min-h-screen", // Modal: full screen
                !isDrawerMode && isCenterAligned ? "h-full" : "",
              )}
            >
              <div
                className={cn(
                  "pointer-events-none",
                  isDrawerMode
                    ? "flex min-h-0 items-end justify-center" // Drawer: bottom aligned
                    : "flex min-h-screen items-center justify-center px-5", // Modal: centered
                )}
              >
                {/* Animated content */}
                <motion.div
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  variants={isDrawerMode ? drawerVariants : modalVariants}
                  transition={{
                    duration: 0.25,
                    ease: "easeInOut",
                  }}
                  className="pointer-events-auto w-full"
                  // Add drag functionality for drawer mode
                  drag={isDrawerMode ? "y" : false}
                  dragConstraints={{ top: 0, bottom: 0 }}
                  dragElastic={{ top: 0, bottom: 0.7 }}
                  onDragStart={isDrawerMode ? handleDragStart : undefined}
                  onDragEnd={isDrawerMode ? handleDragEnd : undefined}
                  dragDirectionLock
                  dragMomentum={false}
                >
                  <DialogPrimitive.DialogContent
                    data-component="DialogPrimitive.DialogContent"
                    onEscapeKeyDown={(e) => {
                      if (!closeOnEsc) {
                        e.preventDefault();
                      } else {
                        onClose();
                      }
                    }}
                    onPointerDownOutside={(e) => {
                      if (!closeOnEsc) {
                        e.preventDefault();
                      }
                    }}
                    onInteractOutside={(e) => {
                      if (!closeOnEsc) {
                        e.preventDefault();
                      }
                    }}
                    className={cn(
                      "relative bottom-0 mx-auto w-full max-w-[520px] overflow-y-auto border-0 bg-transparent p-0 focus:outline-hidden",
                      isDrawerMode
                        ? "max-h-[90vh] rounded-t-2xl" // Drawer: rounded top
                        : "max-h-[95vh] rounded-lg", // Modal: fully rounded
                      theme === "dark" &&
                        "bg-surface-black text-white shadow-[0_0_35px_rgba(255,255,255,0.18)] md:shadow-[0_0_35px_rgba(255,255,255,0.05)]",
                      className,
                    )}
                  >
                    {/* Drag handle for drawer mode */}
                    {/* {isDrawerMode && !noCloseButton && (
                      <div className="absolute left-1/2 top-2 -translate-x-1/2">
                        <div className="h-1 w-12 rounded-full bg-gray-300" />
                      </div>
                    )} */}

                    {title && (
                      <DialogPrimitive.DialogTitle className="mb-6 flex items-center justify-between font-semibold">
                        {title}
                      </DialogPrimitive.DialogTitle>
                    )}
                    {!title && !hasCustomTitle && (
                      <DialogPrimitive.DialogTitle />
                    )}

                    {!noCloseButton && (
                      <button
                        onClick={onClose}
                        className={cn(
                          "absolute top-3 right-3 z-10 text-lg opacity-40 hover:opacity-90 focus:outline-hidden md:top-6 md:right-6",
                          mobileDrawer && "top-5 right-5",
                          closeButtonClass,
                        )}
                      >
                        <X className="h-5 w-5 sm:h-5.5 sm:w-5.5" />
                      </button>
                    )}

                    {children}
                  </DialogPrimitive.DialogContent>
                </motion.div>
              </div>
            </div>
          </DialogPrimitive.DialogPortal>
        </DialogPrimitive.Dialog>
      )}
    </AnimatePresence>
  );
};

export default DrawerDialog;
